import React from 'react';
import { i18n } from 'config/locales';

import { ReasonModel } from 'forms/sections/history/employment/types';
import {
  CommonOnUpdate, CommonScrollIntoView, CommonRequired, CommonOnError
} from 'types/props';
import { AccordionItemProps } from 'components/Form/Accordion/types';
import { setAttribute } from 'components/Form/shared/setAttribute';
import Field from 'components/Form/Field';
import RadioGroup from 'components/Form/RadioGroup';
import Radio from 'components/Form/Radio';
import Show from 'components/Form/Show';
import Textarea from 'components/Form/Textarea';
import DateControl from 'components/Form/DateControl';
import { getSafeDateTime } from 'helpers/date';
import ErrorMessage from 'components/ErrorMessage';
import Comments from 'components/Form/Comments';

interface Props extends Partial<ReasonModel>, AccordionItemProps {
  onUpdate?: CommonOnUpdate
  className?: string
  required?: CommonRequired
  scrollIntoView?: CommonScrollIntoView
  onError?: CommonOnError
  attribute?: string
}

interface State {
  Reason?: Partial<ReasonModel['Reason']>
  Text?: Partial<ReasonModel['Text']>
  Date?: Partial<ReasonModel['Date']>
  Comments?: Partial<ReasonModel['Comments']>
  SeveranceTypeComments?: Partial<ReasonModel['SeveranceTypeComments']>
}

export default class ReasonOptions extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      Reason: props.Reason,
      Text: props.Text,
      Date: props.Date,
      Comments: props.Comments,
      SeveranceTypeComments: props.SeveranceTypeComments,
    };
  }

  onUpdate = (name, values) => {
    this.setState({ [name]: values }, () => {
      if (this.props.onUpdate) {
        this.props.onUpdate({
          name: this.props.name,
          Comments: this.state.Comments,
          SeveranceTypeComments: this.state.SeveranceTypeComments,
          Reason: this.state.Reason,
          Text: this.state.Text,
          Date: this.state.Date,
        });
      }
    });
  };

  updateReason = (values: ReasonModel['Reason']) => {
    this.onUpdate('Reason', values);
  };

  updateText = (values: ReasonModel['Text']) => {
    this.onUpdate('Text', values);
  };

  updateDate = (values: ReasonModel['Date']) => {
    this.onUpdate('Date', values);
  };

  updateComments = (values: ReasonModel['Comments']) => {
    this.onUpdate('Comments', values);
  };

  updateSeveranceTypeComments = (values: ReasonModel['SeveranceTypeComments']) => {
    this.onUpdate('SeveranceTypeComments', values);
  };

  labelText = () => {
    switch ((this.state.Reason || {}).value) {
      case 'Fired':
        return i18n.t('history.employment.default.left.fired.text');
      case 'Quit':
        return i18n.t('history.employment.default.left.quit.text');
      case 'Charges':
        return i18n.t('history.employment.default.left.charges.text');
      case 'Performance':
        return i18n.t('history.employment.default.left.performance.text');
      default:
        break;
    }

    return undefined;
  };

  labelDate = () => {
    switch ((this.state.Reason || {}).value) {
      case 'Fired':
        return i18n.t('history.employment.default.left.fired.date');
      case 'Quit':
        return i18n.t('history.employment.default.left.quit.date');
      case 'Charges':
        return i18n.t('history.employment.default.left.charges.date');
      case 'Performance':
        return i18n.t('history.employment.default.left.performance.date');
      default:
        break;
    }

    return undefined;
  };

  render() {
    const text = this.labelText();
    const date = this.labelDate();
    const attribute = setAttribute(this.props.attribute, `items[${this.props.$index}].Item`);

    const firedDate = getSafeDateTime(this.props.Date);
    const endEmploymentDate = getSafeDateTime(this.props.Dates?.to);

    return (
      <div className={this.props.className}>
        <Field
          attribute={`${attribute}.Reason`}
          title="Select the type of incident"
          adjustFor="big-buttons"
          shrink
          scrollIntoView={this.props.scrollIntoView}
        >
          <RadioGroup
            attribute={`${attribute}.Reason`}
            className="employment-left option-list option-list-vertical"
            selectedValue={(this.state.Reason || {}).value}
            required={this.props.required}
            onError={this.props.onError}
          >
            <Radio
              name="employment_left"
              label={i18n.t('history.employment.default.left.fired.option')}
              value="Fired"
              onUpdate={this.updateReason}
              onError={this.props.onError}
            />
            <Radio
              name="employment_quit"
              label={i18n.t('history.employment.default.left.quit.option')}
              value="Quit"
              onUpdate={this.updateReason}
              onError={this.props.onError}
            />
            <Radio
              name="employment_charges"
              label={i18n.t('history.employment.default.left.charges.option')}
              value="Charges"
              onUpdate={this.updateReason}
              onError={this.props.onError}
            />
            <Radio
              name="employment_performance"
              label={i18n.t('history.employment.default.left.performance.option')}
              value="Performance"
              onUpdate={this.updateReason}
              onError={this.props.onError}
            />
          </RadioGroup>
        </Field>
        <Comments
          title={i18n.t(`history.employment.default.left.severanceTypeComments`)}
          name="SeveranceTypeComments"
          attribute={`${attribute}.SeveranceTypeComments`}
          className="optional-comments"
          {...this.props.SeveranceTypeComments}
          onUpdate={this.updateSeveranceTypeComments}
          onError={this.props.onError}
          required={false}
        />
        <Show when={(this.state.Reason || {}).value}>
          <div>
            <div className="explanation-left">
              <Field
                attribute={`${attribute}.Text`}
                title={text}
                shrink
                scrollIntoView={this.props.scrollIntoView}
              >
                <Textarea
                  attribute={`${attribute}.Text`}
                  name="Text"
                  maxlength="100"
                  {...this.state.Text}
                  onUpdate={this.updateText}
                  onError={this.props.onError}
                  required={this.props.required}
                />
              </Field>
            </div>
            <div className="date-left">
              { // Warning shown
                firedDate?.valid && endEmploymentDate?.valid && firedDate.datetime > endEmploymentDate.datetime && (
                  <ErrorMessage
                    isWarning
                    title="Oops, there’s a problem."
                    message={[
                      'The date for this incident is after the end of this period of employment.',
                      'Please ensure that the date entered is correct and that the incident happened on the date provided.'
                    ]}
                  />
                )
              }
              <Field
                attribute={`${attribute}.Date`}
                title={date}
                shrink
                scrollIntoView={this.props.scrollIntoView}
              >
                <DateControl
                  attribute={`${attribute}.Date`}
                  name="Date"
                  {...this.state.Date}
                  minDateEqualTo
                  onUpdate={this.updateDate}
                  onError={this.props.onError}
                  required={this.props.required}
                  hideDay
                />
              </Field>
            </div>
            <Comments
              title={i18n.t(`history.employment.default.left.optionalComments`)}
              name="Comments"
              attribute={`${attribute}.Comments`}
              className="optional-comments"
              {...this.props.Comments}
              onUpdate={this.updateComments}
              onError={this.props.onError}
              required={false}
            />
          </div>
        </Show>
      </div>
    );
  }
}

ReasonOptions.defaultProps = {
  Dates: {},
  Comments: {},
  SeveranceTypeComments: {},
  onError: (value, arr) => arr,
};
